import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';
import { adminApi } from '../../../api/admin';
import { objectStorageApi } from '../../../api/objectStorage';
import BasicButton from '../../../components/button/BasicButton';
import useAdminDraftGuard from '../useAdminDraftGuard';

const emptyTrack = (trackNumber = 1) => ({
  title: '',
  duration: '',
  trackNumber,
  discNumber: 1,
  contentRating: 'CLEAN',
  artistName: '',
});

const initialForm = {
  provider: 'BANDCAMP',
  sourceUrl: '',
  externalId: '',
  artistName: '',
  title: '',
  albumType: 'ALBUM',
  releaseDate: '',
  recordLabel: '',
  contentRating: 'CLEAN',
  displayRegion: 'GLOBAL',
  exposeSourceUrl: true,
};

const normalizeGenre = (value) =>
  String(value || '')
    .normalize('NFKC')
    .trim()
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase('en-US');

const compareGenre = (value) => normalizeGenre(value).replace(/[\s_\-‐‑‒–—]+/g, '');

const emptyGenre = (nameEn = '', sourceTag = '') => ({
  nameKo: '',
  nameEn,
  sourceTag,
  catalogId: null,
});

const durationToMs = (value) => {
  const match = String(value)
    .trim()
    .match(/^(\d{1,3}):(\d{2})$/);
  if (!match || Number(match[2]) > 59) return null;
  const milliseconds = (Number(match[1]) * 60 + Number(match[2])) * 1000;
  return milliseconds > 0 ? milliseconds : null;
};

const durationFromMs = (value) => {
  const totalSeconds = Math.max(0, Math.round(Number(value) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
};

const DirectCatalogManagement = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [genreCatalog, setGenreCatalog] = useState([]);
  const [genreRows, setGenreRows] = useState([]);
  const [genreCatalogError, setGenreCatalogError] = useState('');
  const [tracks, setTracks] = useState([emptyTrack()]);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState('');
  const [uploadedCoverUrl, setUploadedCoverUrl] = useState('');
  const [preflight, setPreflight] = useState(null);
  const [metadataWarnings, setMetadataWarnings] = useState([]);
  const [metadataReviewed, setMetadataReviewed] = useState(false);
  const [sourceCoverPreview, setSourceCoverPreview] = useState('');
  const [sourceCoverAccepted, setSourceCoverAccepted] = useState(false);
  const [metadataBusy, setMetadataBusy] = useState(false);
  const [artistChoice, setArtistChoice] = useState('new');
  const [duplicateConfirmed, setDuplicateConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const draft = useAdminDraftGuard(
    { form, genreRows, tracks, coverFile, uploadedCoverUrl, sourceCoverAccepted, artistChoice },
    { busy: busy || metadataBusy },
  );

  useEffect(() => {
    let active = true;
    adminApi
      .getDirectCatalogGenres()
      .then((response) => {
        if (!active) return;
        setGenreCatalog(response.data || []);
        setGenreCatalogError('');
      })
      .catch(() => {
        if (!active) return;
        setGenreCatalogError(
          '공용 장르 사전을 불러오지 못했습니다. 새 장르 등록 전 서버 연결을 확인해주세요.',
        );
      });
    return () => {
      active = false;
    };
  }, []);

  const artistCandidates = preflight?.artistCandidates || [];
  const duplicateCandidates = preflight?.duplicateCandidates || [];
  const canCreate =
    preflight &&
    !preflight.exactSourceAlbumId &&
    (artistCandidates.length === 0 || artistChoice) &&
    (duplicateCandidates.length === 0 || duplicateConfirmed) &&
    Boolean(coverFile || uploadedCoverUrl || sourceCoverAccepted);

  const invalidatePreflight = () => {
    setPreflight(null);
    setDuplicateConfirmed(false);
  };

  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    invalidatePreflight();
  };

  const updateTrack = (index, key, value) => {
    setTracks((current) =>
      current.map((track, trackIndex) =>
        trackIndex === index ? { ...track, [key]: value } : track,
      ),
    );
    invalidatePreflight();
  };

  const updateGenre = (index, key, value) => {
    setGenreRows((current) =>
      current.map((genre, genreIndex) => {
        if (genreIndex !== index) return genre;
        // Never replace the other language while typing, including Korean IME
        // intermediate matches. Only explicit catalog selection replaces a pair.
        return { ...genre, [key]: value, catalogId: null };
      }),
    );
    invalidatePreflight();
  };

  const selectGenre = (index, option) => {
    if (!option || typeof option === 'string') return;
    setGenreRows((current) =>
      current.map((genre, genreIndex) =>
        genreIndex === index
          ? {
              ...genre,
              nameKo: option.nameKo,
              nameEn: option.nameEn,
              catalogId: option.id ?? `built-in:${option.nameKo}`,
            }
          : genre,
      ),
    );
    invalidatePreflight();
  };

  const removeGenre = (index) => {
    setGenreRows((current) => current.filter((_, genreIndex) => genreIndex !== index));
    invalidatePreflight();
  };

  const addGenre = () => {
    setGenreRows((current) => [...current, emptyGenre()]);
    invalidatePreflight();
  };

  const addTrack = () => {
    setTracks((current) => [...current, emptyTrack(current.length + 1)]);
    invalidatePreflight();
  };

  const removeTrack = (index) => {
    setTracks((current) =>
      current
        .filter((_, trackIndex) => trackIndex !== index)
        .map((track, trackIndex) => ({ ...track, trackNumber: trackIndex + 1 })),
    );
    invalidatePreflight();
  };

  const onCoverChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/') || file.size > 5 * 1024 * 1024) {
      setError('커버 이미지는 PNG/JPG 형식, 5MB 이하만 사용할 수 있습니다.');
      return;
    }
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
    setUploadedCoverUrl('');
    setSourceCoverAccepted(false);
    setError('');
  };

  const normalizedTracks = useMemo(
    () =>
      tracks.map((track) => ({
        title: track.title.trim(),
        durationMs: durationToMs(track.duration),
        trackNumber: Number(track.trackNumber),
        discNumber: Number(track.discNumber),
        contentRating: track.contentRating,
        artistName: track.artistName.trim() || null,
      })),
    [tracks],
  );

  const validateClient = () => {
    if (!form.sourceUrl.trim().startsWith('https://')) return 'HTTPS 원본 URL을 입력해주세요.';
    if (!form.artistName.trim() || !form.title.trim() || !form.releaseDate) {
      return '아티스트, 앨범 제목, 발매일을 모두 입력해주세요.';
    }
    if (normalizedTracks.some((track) => !track.title || !track.durationMs)) {
      return '모든 트랙의 제목과 러닝타임(mm:ss)을 확인해주세요.';
    }
    if (genreCatalogError) return genreCatalogError;
    if (genreRows.some((genre) => !genre.nameKo.trim() || !genre.nameEn.trim())) {
      return '모든 장르의 한국어명과 영어명을 입력하거나 장르가 아닌 원본 태그를 삭제해주세요.';
    }
    const normalizedKoreanGenres = genreRows.map((genre) => normalizeGenre(genre.nameKo));
    const normalizedEnglishGenres = genreRows.map((genre) => normalizeGenre(genre.nameEn));
    if (new Set(normalizedKoreanGenres).size !== normalizedKoreanGenres.length) {
      return '같은 한국어 장르가 두 번 입력됐습니다.';
    }
    if (new Set(normalizedEnglishGenres).size !== normalizedEnglishGenres.length) {
      return '같은 영어 장르가 두 번 입력됐습니다.';
    }
    if (new Set(genreRows.map((genre) => compareGenre(genre.nameKo))).size !== genreRows.length) {
      return '띄어쓰기나 하이픈만 다른 한국어 장르가 중복 입력됐습니다.';
    }
    if (new Set(genreRows.map((genre) => compareGenre(genre.nameEn))).size !== genreRows.length) {
      return '띄어쓰기나 하이픈만 다른 영어 장르가 중복 입력됐습니다.';
    }
    const discs = new Map();
    normalizedTracks.forEach((track) => {
      const numbers = discs.get(track.discNumber) || [];
      numbers.push(track.trackNumber);
      discs.set(track.discNumber, numbers);
    });
    for (const numbers of discs.values()) {
      const sorted = [...numbers].sort((a, b) => a - b);
      if (sorted.some((number, index) => number !== index + 1)) {
        return '트랙 번호는 디스크별로 1부터 연속이어야 합니다.';
      }
    }
    return '';
  };

  const uploadCoverIfNeeded = async () => {
    if (uploadedCoverUrl) return uploadedCoverUrl;
    if (sourceCoverAccepted) {
      const response = await adminApi.importDirectCatalogSourceCover({
        provider: form.provider,
        sourceUrl: form.sourceUrl.trim(),
      });
      const url = response.data?.url;
      if (!url) throw new Error('원본 커버 복사 결과를 확인할 수 없습니다.');
      setUploadedCoverUrl(url);
      return url;
    }
    if (!coverFile) throw new Error('사용할 커버 이미지를 선택해주세요.');
    const imageFormData = new FormData();
    imageFormData.append('image', coverFile);
    const response = await objectStorageApi.uploadImage(imageFormData);
    const url = response.data?.url;
    if (!url) throw new Error('커버 이미지 업로드 결과를 확인할 수 없습니다.');
    setUploadedCoverUrl(url);
    return url;
  };

  const loadSourceMetadata = async () => {
    if (!form.sourceUrl.trim().startsWith('https://')) {
      setError('HTTPS Bandcamp 앨범 또는 트랙 URL을 입력해주세요.');
      return;
    }
    setMetadataBusy(true);
    setError('');
    setMetadataWarnings([]);
    setMetadataReviewed(false);
    setSourceCoverPreview('');
    setSourceCoverAccepted(false);
    try {
      const response = await adminApi.getDirectCatalogMetadata({
        provider: form.provider,
        sourceUrl: form.sourceUrl.trim(),
      });
      const draft = response.data;
      const sourceGenres = (draft.sourceGenres || []).map((sourceTag) => {
        const matches = genreCatalog.filter(
          (genre) => normalizeGenre(genre.nameEn) === normalizeGenre(sourceTag),
        );
        if (matches.length !== 1) return emptyGenre(sourceTag, sourceTag);
        const match = matches[0];
        return {
          nameKo: match.nameKo,
          nameEn: match.nameEn,
          sourceTag,
          catalogId: match.id ?? `built-in:${match.nameKo}`,
        };
      });
      setForm((current) => ({
        ...current,
        sourceUrl: draft.canonicalSourceUrl,
        externalId: draft.externalId || '',
        artistName: draft.artistName,
        title: draft.title,
        albumType: draft.suggestedAlbumType,
        releaseDate: draft.releaseDate,
        recordLabel: draft.recordLabel || '',
      }));
      setGenreRows(sourceGenres);
      setTracks(
        draft.tracks.map((track) => ({
          title: track.title,
          duration: track.durationMs == null ? '' : durationFromMs(track.durationMs),
          trackNumber: track.trackNumber,
          discNumber: track.discNumber,
          contentRating: 'CLEAN',
          artistName: '',
        })),
      );
      setMetadataWarnings(draft.warnings || []);
      setSourceCoverPreview(draft.sourceImageUrl || '');
      setCoverFile(null);
      setCoverPreview('');
      setUploadedCoverUrl('');
      setSourceCoverAccepted(false);
      invalidatePreflight();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setMetadataBusy(false);
    }
  };

  const buildPayload = (imageUrl, includeConfirmations) => ({
    provider: form.provider,
    sourceUrl: form.sourceUrl.trim(),
    externalId: form.externalId.trim() || null,
    existingArtistCandidateKey:
      includeConfirmations && artistChoice.startsWith('existing:')
        ? artistChoice.slice('existing:'.length)
        : null,
    artistName: form.artistName.trim(),
    title: form.title.trim(),
    albumType: form.albumType,
    imageUrl,
    releaseDate: form.releaseDate,
    recordLabel: form.recordLabel.trim() || null,
    genres: [],
    genreMappings: genreRows.map((genre) => ({
      nameKo: genre.nameKo.trim(),
      nameEn: genre.nameEn.trim(),
    })),
    contentRating: form.contentRating,
    displayRegion: form.displayRegion,
    userVisibleUrls: form.exposeSourceUrl
      ? { [form.provider.toLowerCase()]: form.sourceUrl.trim() }
      : {},
    tracks: normalizedTracks,
    reviewedDuplicateCandidateIds: includeConfirmations
      ? duplicateCandidates.map((candidate) => candidate.albumId)
      : [],
    reviewedArtistCandidateKeys:
      includeConfirmations && artistChoice === 'new'
        ? artistCandidates.map((candidate) => candidate.candidateKey)
        : [],
  });

  const runPreflight = async () => {
    const validationError = validateClient();
    if (validationError) {
      setError(validationError);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await adminApi.preflightDirectCatalogAlbum(buildPayload('', false));
      const result = response.data;
      setPreflight(result);
      setArtistChoice(result.artistCandidates?.length ? '' : 'new');
      setDuplicateConfirmed(false);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const createAlbum = async () => {
    if (!canCreate) return;
    setBusy(true);
    setError('');
    try {
      const imageUrl = await uploadCoverIfNeeded();
      const response = await adminApi.createDirectCatalogAlbum(buildPayload(imageUrl, true));
      const createdAlbum = response.data;
      const albumPath = createdAlbum.slug
        ? `/reviews/${createdAlbum.slug}`
        : `/reviews/${createdAlbum.albumSource.toLowerCase()}/${createdAlbum.sourceId}`;
      draft.markClean();
      navigate(albumPath, { state: { directCatalogCreated: true } });
    } catch (requestError) {
      setError(requestError.message);
      setPreflight(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={4} sx={{ maxWidth: 980 }}>
      <Box>
        <Typography variant="h5" fontWeight={700}>
          음악 직접 등록
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          Apple Music에 없는 발매작을 내부 음악 데이터로 등록합니다. 사전검사 결과를 확인하기 전에는
          저장되지 않습니다.
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
        <Stack spacing={3}>
          <Typography variant="h6">원본과 앨범 정보</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField label="원본 제공자" value={form.provider} disabled fullWidth />
            <TextField
              label="외부 앨범 ID (선택)"
              value={form.externalId}
              onChange={(event) => updateForm('externalId', event.target.value)}
              fullWidth
            />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
              label="원본 페이지 HTTPS URL"
              value={form.sourceUrl}
              onChange={(event) => {
                updateForm('sourceUrl', event.target.value);
                setMetadataWarnings([]);
                setMetadataReviewed(false);
                setSourceCoverPreview('');
                setSourceCoverAccepted(false);
                setUploadedCoverUrl('');
              }}
              fullWidth
              required
            />
            <BasicButton
              variant="outlined"
              onClick={loadSourceMetadata}
              disabled={busy || metadataBusy}
              sx={{ minWidth: { md: 180 }, flexShrink: 0 }}
            >
              {metadataBusy ? '불러오는 중…' : '원본 정보 불러오기'}
            </BasicButton>
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="아티스트"
              value={form.artistName}
              onChange={(event) => updateForm('artistName', event.target.value)}
              fullWidth
              required
            />
            <TextField
              label="앨범 제목"
              value={form.title}
              onChange={(event) => updateForm('title', event.target.value)}
              fullWidth
              required
            />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>유형</InputLabel>
              <Select
                label="유형"
                value={form.albumType}
                onChange={(event) => updateForm('albumType', event.target.value)}
              >
                <MenuItem value="ALBUM">앨범</MenuItem>
                <MenuItem value="SINGLE">싱글</MenuItem>
              </Select>
            </FormControl>
            <TextField
              type="date"
              label="발매일"
              InputLabelProps={{ shrink: true }}
              value={form.releaseDate}
              onChange={(event) => updateForm('releaseDate', event.target.value)}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>국내·해외 구분</InputLabel>
              <Select
                label="국내·해외 구분"
                value={form.displayRegion}
                onChange={(event) => updateForm('displayRegion', event.target.value)}
              >
                <MenuItem value="KR">국내</MenuItem>
                <MenuItem value="GLOBAL">해외</MenuItem>
                <MenuItem value="MIXED">혼합</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="레이블 (선택)"
              value={form.recordLabel}
              onChange={(event) => updateForm('recordLabel', event.target.value)}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>콘텐츠 등급</InputLabel>
              <Select
                label="콘텐츠 등급"
                value={form.contentRating}
                onChange={(event) => updateForm('contentRating', event.target.value)}
              >
                <MenuItem value="CLEAN">일반</MenuItem>
                <MenuItem value="EXPLICIT">청소년 유해</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <Stack spacing={1.5}>
            <Box>
              <Typography fontWeight={600}>장르</Typography>
              <Typography variant="body2" color="text.secondary">
                원본 영문 태그는 후보입니다. 실제 장르만 남기고 한국어·영어 이름을 한 쌍으로
                확인해주세요. 기존 장르는 자동으로 연결되며 새 장르는 등록 완료 시 공용 사전에
                추가됩니다.
              </Typography>
            </Box>
            {genreCatalogError && <Alert severity="error">{genreCatalogError}</Alert>}
            {genreRows.length === 0 && (
              <Alert severity="info">
                자동으로 확인된 장르가 없습니다. 필요하면 장르 추가를 눌러 입력해주세요.
              </Alert>
            )}
            {genreRows.map((genre, index) => (
              <Paper
                key={`${genre.sourceTag || 'genre'}-${index}`}
                variant="outlined"
                sx={{ p: 1.5 }}
              >
                <Stack spacing={1}>
                  {genre.sourceTag && (
                    <Typography variant="caption" color="text.secondary">
                      원본 태그: {genre.sourceTag}
                    </Typography>
                  )}
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="center">
                    <TextField
                      label="한국어 장르명"
                      value={genre.nameKo}
                      onChange={(event) => updateGenre(index, 'nameKo', event.target.value)}
                      fullWidth
                      required
                    />
                    <Autocomplete
                      freeSolo
                      fullWidth
                      options={genreCatalog}
                      value={null}
                      inputValue={genre.nameEn}
                      getOptionLabel={(option) =>
                        typeof option === 'string' ? option : `${option.nameEn} · ${option.nameKo}`
                      }
                      isOptionEqualToValue={(option, value) => option.nameKo === value.nameKo}
                      onChange={(_, option) => selectGenre(index, option)}
                      onInputChange={(_, value, reason) => {
                        if (reason !== 'reset') updateGenre(index, 'nameEn', value);
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="영어 장르명" required />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} key={`${option.nameKo}-${option.nameEn}`}>
                          {option.nameEn} · {option.nameKo}
                        </Box>
                      )}
                    />
                    <IconButton
                      aria-label={`${genre.nameEn || genre.nameKo || '장르'} 삭제`}
                      onClick={() => removeGenre(index)}
                    >
                      <DeleteOutline />
                    </IconButton>
                  </Stack>
                  <Typography
                    variant="caption"
                    color={genre.catalogId ? 'success.main' : 'text.secondary'}
                  >
                    {genre.catalogId
                      ? '공용 장르 사전의 기존 항목을 사용합니다.'
                      : '입력 중에는 다른 언어를 바꾸지 않습니다. 기존 장르는 영어 입력란의 목록에서 선택하세요. 등록 시 장르 쌍을 검증합니다.'}
                  </Typography>
                </Stack>
              </Paper>
            ))}
            <BasicButton
              variant="outlined"
              startIcon={<Add />}
              onClick={addGenre}
              sx={{ alignSelf: 'flex-start' }}
            >
              장르 추가
            </BasicButton>
          </Stack>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.exposeSourceUrl}
                onChange={(event) => updateForm('exposeSourceUrl', event.target.checked)}
              />
            }
            label="앨범 상세에 원본 페이지 듣기 링크 표시"
          />
          <Stack spacing={1}>
            <Typography fontWeight={600}>커버 이미지 (PNG/JPG, 5MB 이하)</Typography>
            <Typography variant="body2" color="text.secondary">
              사전검사에는 업로드하지 않고, 결과 확인 뒤 등록할 때만 전송합니다.
            </Typography>
            {sourceCoverPreview && !coverPreview && (
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  Bandcamp 원본 커버 미리보기 — 고화질·정확성을 확인한 뒤 그대로 사용할 수 있습니다.
                </Typography>
                <Box
                  component="img"
                  src={sourceCoverPreview}
                  alt="Bandcamp 원본 커버 미리보기"
                  sx={{ width: 180, height: 180, objectFit: 'cover', borderRadius: 1 }}
                />
                <BasicButton
                  variant={sourceCoverAccepted ? 'contained' : 'outlined'}
                  onClick={() => {
                    setSourceCoverAccepted(true);
                    setCoverFile(null);
                    setCoverPreview('');
                    setUploadedCoverUrl('');
                    setError('');
                  }}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {sourceCoverAccepted ? '원본 커버 사용 확인됨' : '이 원본 커버 사용'}
                </BasicButton>
                {sourceCoverAccepted && (
                  <Alert severity="success">
                    최종 등록할 때 검증된 원본 커버를 포스트팝 저장소로 복사합니다.
                  </Alert>
                )}
              </Stack>
            )}
            <BasicButton variant="outlined" component="label" sx={{ alignSelf: 'flex-start' }}>
              이미지 선택
              <input hidden type="file" accept="image/png,image/jpeg" onChange={onCoverChange} />
            </BasicButton>
            {coverPreview && (
              <Box
                component="img"
                src={coverPreview}
                alt="커버 미리보기"
                sx={{ width: 180, height: 180, objectFit: 'cover', borderRadius: 1 }}
              />
            )}
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
        <Stack spacing={2}>
          <Typography variant="h6">트랙</Typography>
          {tracks.map((track, index) => (
            <Stack
              key={index}
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ md: 'center' }}
            >
              <TextField
                label="Disc"
                type="number"
                value={track.discNumber}
                onChange={(event) => updateTrack(index, 'discNumber', event.target.value)}
                sx={{ width: { md: 72 }, flexShrink: { md: 0 } }}
              />
              <TextField
                label="No."
                type="number"
                value={track.trackNumber}
                onChange={(event) => updateTrack(index, 'trackNumber', event.target.value)}
                sx={{ width: { md: 72 }, flexShrink: { md: 0 } }}
              />
              <TextField
                label="트랙 제목"
                value={track.title}
                onChange={(event) => updateTrack(index, 'title', event.target.value)}
                fullWidth
                sx={{ minWidth: { md: 150 } }}
              />
              <TextField
                label="러닝타임 mm:ss"
                value={track.duration}
                onChange={(event) => updateTrack(index, 'duration', event.target.value)}
                sx={{ width: { md: 150 }, flexShrink: { md: 0 } }}
              />
              <TextField
                label="별도 아티스트 (선택)"
                value={track.artistName}
                onChange={(event) => updateTrack(index, 'artistName', event.target.value)}
                fullWidth
                sx={{ minWidth: { md: 150 } }}
              />
              <FormControl sx={{ width: { md: 120 }, flexShrink: { md: 0 } }}>
                <InputLabel>등급</InputLabel>
                <Select
                  label="등급"
                  value={track.contentRating}
                  onChange={(event) => updateTrack(index, 'contentRating', event.target.value)}
                >
                  <MenuItem value="CLEAN">일반</MenuItem>
                  <MenuItem value="EXPLICIT">유해</MenuItem>
                </Select>
              </FormControl>
              <BasicButton
                variant="outlined"
                onClick={() => removeTrack(index)}
                disabled={tracks.length === 1}
                aria-label={`${index + 1}번 트랙 삭제`}
                sx={{ minWidth: { md: 52 }, flexShrink: { md: 0 } }}
              >
                <DeleteOutline />
              </BasicButton>
            </Stack>
          ))}
          <BasicButton
            variant="outlined"
            startIcon={<Add />}
            onClick={addTrack}
            sx={{ alignSelf: 'flex-start' }}
          >
            트랙 추가
          </BasicButton>
        </Stack>
      </Paper>

      <Stack spacing={2}>
        {metadataWarnings.length > 0 && (
          <Alert severity="warning">
            <Stack component="ul" sx={{ my: 0, pl: 2 }}>
              {metadataWarnings.map((warning) => (
                <Typography component="li" variant="body2" key={warning}>
                  {warning}
                </Typography>
              ))}
            </Stack>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  checked={metadataReviewed}
                  onChange={(event) => setMetadataReviewed(event.target.checked)}
                />
              }
              label="원본 페이지와 자동 입력값을 대조하고 장르·레이블·국내·해외 구분·유형·트랙을 검토했습니다."
            />
          </Alert>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        <BasicButton
          onClick={runPreflight}
          disabled={busy || metadataBusy || (metadataWarnings.length > 0 && !metadataReviewed)}
        >
          {busy ? '확인 중…' : '1. 사전검사'}
        </BasicButton>
      </Stack>

      {preflight && (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
          <Stack spacing={3}>
            <Typography variant="h6">사전검사 결과</Typography>
            <Typography>정규 원본 URL: {preflight.canonicalSourceUrl}</Typography>
            {preflight.exactSourceAlbumId && (
              <Alert severity="error">
                같은 원본 URL이 albumId {preflight.exactSourceAlbumId}에 이미 등록되어 있습니다.
              </Alert>
            )}
            {!preflight.exactSourceAlbumId && artistCandidates.length === 0 && (
              <Alert severity="success">
                동일명 아티스트 후보가 없습니다. 새 아티스트로 등록합니다.
              </Alert>
            )}
            {artistCandidates.length > 0 && (
              <Box>
                <Typography fontWeight={700}>동일명 아티스트 선택</Typography>
                <RadioGroup
                  value={artistChoice}
                  onChange={(event) => setArtistChoice(event.target.value)}
                >
                  {artistCandidates.map((candidate) => (
                    <FormControlLabel
                      key={candidate.candidateKey}
                      value={`existing:${candidate.candidateKey}`}
                      control={<Radio />}
                      label={`기존 아티스트 사용 — ${candidate.artistId ? `#${candidate.artistId} / ` : ''}${candidate.artistSource} / ${candidate.name} (${candidate.sourceId})`}
                    />
                  ))}
                  <FormControlLabel
                    value="new"
                    control={<Radio />}
                    label="동명이인으로 새 아티스트 생성"
                  />
                </RadioGroup>
              </Box>
            )}
            {duplicateCandidates.length > 0 && (
              <Box>
                <Alert severity="warning">
                  제목·아티스트가 같은 후보가 있습니다. 발매일이 다르면 원발매·재발매 여부도
                  확인하세요.
                </Alert>
                <Stack component="ul" sx={{ mt: 1 }}>
                  {duplicateCandidates.map((candidate) => (
                    <Typography component="li" key={candidate.albumId}>
                      #{candidate.albumId} · {candidate.artistName} — {candidate.title} (
                      {candidate.releaseDate}) · {candidate.albumSource}
                    </Typography>
                  ))}
                </Stack>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={duplicateConfirmed}
                      onChange={(event) => setDuplicateConfirmed(event.target.checked)}
                    />
                  }
                  label="모든 후보를 확인했으며 기존 앨범과 병합하지 않고 별도 앨범으로 등록합니다."
                />
              </Box>
            )}
            {!preflight.exactSourceAlbumId && duplicateCandidates.length === 0 && (
              <Alert severity="success">메타데이터 중복 후보가 없습니다.</Alert>
            )}
            <BasicButton onClick={createAlbum} disabled={busy || !canCreate}>
              {busy ? '등록 중…' : '2. 확인 후 등록'}
            </BasicButton>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
};

export default DirectCatalogManagement;
